import { useEffect, useRef, useState } from 'react';

import { VideoCameraIcon } from '@ergeon/icons/hero/outline';

import { cn } from '@/lib/utils';

// One reserved box for both states: the placeholder icon and the live preview
// share the same aspect-video frame, so granting the camera changes what is
// inside the card, never its height — nothing below it moves.
export default function LobbyVideo({ videoStream }: { videoStream?: MediaStream }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // Faded in when frames actually render, not when the element mounts: a
  // mount-time animation plays over a still-black box and the picture then
  // pops in after it, which reads worse than no animation at all.
  const [isPlaying, setIsPlaying] = useState(false);

  useEffect(function assignVideoStreamToVideoElement() {
    if (videoStream && videoRef.current) {
      videoRef.current.srcObject = videoStream;
    }
    if (!videoStream) {
      setIsPlaying(false);
    }
  }, [videoStream]);

  const handlePlaying = () => setIsPlaying(true);

  return (
    <div
      className={cn(
        'relative aspect-video w-full overflow-hidden rounded-2xl',
        'bg-gray-800 shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-white/10',
      )}>
      <div className="absolute inset-0 flex items-center justify-center">
        <VideoCameraIcon className="size-16 text-gray-500" />
      </div>
      {videoStream && (
        <video
          autoPlay
          // The lobby preview is always the front camera looking at the
          // customer, so it has to read like a mirror, same as the self-view
          // in the call.
          className={`absolute inset-0 size-full -scale-x-100 object-cover transition-opacity duration-200 ${
            isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
          muted
          onPlaying={handlePlaying}
          playsInline
          ref={videoRef} />
      )}
    </div>
  );
}
